// Builds the Firestore event payload from the form state. Isolating
// this keeps the submit handler in `CreateEventModal` focused on
// orchestration (network call, toast, navigation) instead of shape.
//
// Two shapes come out of here:
//   • sharedFields      — safe for both create and update.
//   • buildCreatePayload — extends sharedFields with server-managed
//                          fields that only apply on creation.

import { Timestamp, doc } from "firebase/firestore";
import { db } from "../../../firebase/firebase-config";
import { CONFIRMATION_LEAD_HOURS, DEFAULT_EVENT_IMAGE } from "./constants";

// Pull out the heavy booking math so we only compute it once.
const computeBookingMath = (formData, partnerVenues) => {
  const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
  const endDateTime = new Date(`${formData.endDate}T${formData.endTime || "23:59"}`);

  const selectedPartner = partnerVenues.find((v) => v.id === formData.partnerVenueId) || null;
  const venuePricePerHour = Number(selectedPartner?.minPricePerHour) || 0;
  const bookingHours = (endDateTime - startDateTime) / (1000 * 60 * 60);
  const venueMinimumRevenue =
    selectedPartner && bookingHours > 0
      ? Math.round(bookingHours * venuePricePerHour * 100) / 100
      : 0;
  const confirmationDeadline = selectedPartner
    ? Timestamp.fromDate(new Date(startDateTime.getTime() - CONFIRMATION_LEAD_HOURS * 60 * 60 * 1000))
    : null;

  return {
    startDateTime,
    endDateTime,
    selectedPartner,
    venuePricePerHour,
    bookingHours,
    venueMinimumRevenue,
    confirmationDeadline,
  };
};

// Fields valid for both create AND update (no host/members/createdAt so
// an update can't clobber ownership or history). Note that
// `venueApprovalStatus` is intentionally NOT in sharedFields — it is owned
// by the venue owner's accept/reject action and should never be touched by
// a routine "edit event" update from the host.
export const buildSharedEventFields = (formData, partnerVenues) => {
  const math = computeBookingMath(formData, partnerVenues);

  return {
    name: formData.name,
    description: formData.description,
    location: formData.location,
    latitude: formData.latitude ?? null,
    longitude: formData.longitude ?? null,
    partnerVenueId: formData.partnerVenueId || null,
    partnerVenueName: formData.partnerVenueName || null,
    venuePricePerHour: math.selectedPartner ? math.venuePricePerHour : null,
    venueBookingHours: math.selectedPartner ? math.bookingHours : null,
    venueMinimumRevenue: math.selectedPartner ? math.venueMinimumRevenue : null,
    confirmationDeadline: math.confirmationDeadline,
    startTime: Timestamp.fromDate(math.startDateTime),
    endTime: Timestamp.fromDate(math.endDateTime),
    cost: Number(formData.cost) || 0,
    maxMembers: Number(formData.maxMembers) || 10,
    guestsToBring: formData.guestsToBring || "",
    interests: formData.interests || [],
    imageUrl: formData.imageUrl || DEFAULT_EVENT_IMAGE,
  };
};

// Extends sharedFields with everything needed at create-time (host,
// member list, server timestamps, etc).
export const buildCreateEventPayload = ({ formData, partnerVenues, user, eventId }) => {
  const shared = buildSharedEventFields(formData, partnerVenues);
  const math = computeBookingMath(formData, partnerVenues);

  // If the host picked a partner venue, the event is blocked until the
  // venue owner accepts. We stamp the owner's uid onto the event so
  // Firestore rules / queries can filter "requests for me".
  // Special case: the host IS the venue owner — there's no one else to
  // wait on, so we auto-approve and stamp the response timestamp now.
  const venueOwnerUid = math.selectedPartner?.userId || null;
  const isOwnVenueBooking =
    !!math.selectedPartner && !!venueOwnerUid && venueOwnerUid === user.uid;
  const venueApprovalFields = math.selectedPartner
    ? {
      venueApprovalStatus: isOwnVenueBooking ? "approved" : "pending",
      venueOwnerUid,
      venueRespondedAt: isOwnVenueBooking ? Timestamp.now() : null,
    }
    : {
      venueApprovalStatus: null,
      venueOwnerUid: null,
      venueRespondedAt: null,
    };

  return {
    id: eventId,
    ...shared,
    ...venueApprovalFields,
    lengthDescription: "",
    host: user.uid,
    members: [doc(db, "users", user.uid)],
    online: false,
    showAdd: true,
    createdAt: Timestamp.now(),
    hostURL: null,
    joinURL: null,
    languages: [],
    // Money management counters — all in cents, all maintained
    // transactionally by `recordTicketPurchase`. Initializing to 0 here
    // means we never have to defend against undefined in the split math
    // and the venue/producer/platform totals always reconcile.
    revenueCollected: 0,
    venueRevenueCollected: 0,
    producerRevenueCollected: 0,
    platformRevenueCollected: 0,
  };
};
